import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { ThreeDots } from "react-loader-spinner";
import { motion } from "framer-motion";
import moment from "moment-timezone";
import Calendar from "../../../helpers/dateComponents/Calendar.jsx";
import { setHeaderText } from "../../../features/headerText/headerTextSlice";
import UpArrowImage from "../../../images/icons/up-down-arrow.svg";

const TOAST_CONFIG = {
  position: "bottom-right",
  autoClose: 2000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const INVITE_STATUS_OPTIONS = [
  { label: "Pending", value: "0" },
  { label: "Accepted", value: "1" },
  { label: "Expired", value: "2" },
];

const ROWS_PER_PAGE_OPTIONS = [10, 20, 30, 40];
const DEFAULT_ROWS_PER_PAGE = 10;
const PAGINATION_WINDOW_SIZE = 2;

const formatDate = (date) =>
  date ? moment(date).format("MMMM DD, YYYY") : "N/A";

const formatDateRange = (range) => {
  if (!range || !range[0] || !range[1]) return "";
  return `${new Date(range[0]).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })} to ${new Date(range[1]).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })}`;
};

const renderStatus = (invite_status) => {
  let cls = "";
  let label = "";
  if (invite_status === "0" || invite_status === 0) { cls = "pending"; label = "Pending"; }
  else if (invite_status === "1" || invite_status === 1) { cls = "manual"; label = "Accepted"; }
  else if (invite_status === "2" || invite_status === 2) { cls = "overdue"; label = "Expired"; }
  else { cls = "pending"; label = "Pending"; }
  return <span className={`payment-status ${cls}`}>{label}</span>;
};

const TenantInviteHistory = () => {
  const dispatch = useDispatch();
  const token = useSelector((s) => s.masterAuth.masterUser?.token);

  useEffect(() => {
    dispatch(setHeaderText("Tenant Invite History"));
  }, [dispatch]);

  const [invites, setInvites] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [selectedStatus, setSelectedStatus] = useState(INVITE_STATUS_OPTIONS.map((s) => s.value));
  const [selectAllStatus, setSelectAllStatus] = useState(true);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  const [selectedRange, setSelectedRange] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);

  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [selectedInvites, setSelectedInvites] = useState(new Set());
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [isResending, setIsResending] = useState(false);

  const [showingDropdownOpen, setShowingDropdownOpen] = useState(false);

  const statusDropdownRef = useRef(null);
  const calendarRef = useRef(null);
  const showingDropdownRef = useRef(null);

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchFilter); setCurrentPage(1); }, 500);
    return () => clearTimeout(t);
  }, [searchFilter]);

  const fetchHistory = useCallback(async () => {
    try {
      setIsUpdating(true);
      const resp = await fetch("/api/master/tenant-invite/history", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          page: currentPage,
          limit: rowsPerPage,
          searchFilter: debouncedSearch,
          invite_status: selectedStatus.length === INVITE_STATUS_OPTIONS.length ? [] : selectedStatus,
          startDate: selectedRange ? selectedRange[0] : null,
          endDate: selectedRange ? selectedRange[1] : null,
          sort_by: sortBy,
          sort_order: sortOrder,
        }),
      });
      const data = await resp.json();
      setInvites(data.invitedTenants || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Failed to fetch invite history", TOAST_CONFIG);
    } finally {
      setIsUpdating(false);
      setIsLoading(false);
    }
  }, [token, currentPage, rowsPerPage, debouncedSearch, selectedStatus, selectedRange, sortBy, sortOrder]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Outside click
  useEffect(() => {
    const handler = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target))
        setStatusDropdownOpen(false);
      if (calendarRef.current && !calendarRef.current.contains(e.target))
        setShowCalendar(false);
      if (showingDropdownRef.current && !showingDropdownRef.current.contains(e.target))
        setShowingDropdownOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const totalPages = Math.ceil(total / rowsPerPage);

  const handleSort = (col) => {
    const newOrder = sortBy === col && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(col);
    setSortOrder(newOrder);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setSelectedInvites(new Set());
    }
  };

  const toggleSelectAll = () => {
    if (selectedInvites.size === invites.length) setSelectedInvites(new Set());
    else setSelectedInvites(new Set(invites.map((r) => r._id)));
  };

  const toggleSelect = (id) => {
    setSelectedInvites((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    try {
      setIsDeleting(true);
      const resp = await fetch("/api/master/tenant-invite/bulk-delete", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: Array.from(selectedInvites) }),
      });
      const data = await resp.json();
      if (resp.ok) {
        toast.success(data.message || "Deleted successfully", TOAST_CONFIG);
        setSelectedInvites(new Set());
        setIsDeleteConfirmOpen(false);
        fetchHistory();
      } else {
        toast.error(data.message || "Delete failed", TOAST_CONFIG);
      }
    } catch {
      toast.error("Something went wrong", TOAST_CONFIG);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResend = async () => {
    try {
      setIsResending(true);
      const resp = await fetch("/api/master/tenant-invite/resend-single", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ invEmail: resendEmail }),
      });
      const data = await resp.json();
      if (resp.ok) {
        if (data.status === "already_registered") {
          toast.warning("Tenant already registered", TOAST_CONFIG);
        } else {
          toast.success("Invitation resent successfully!", TOAST_CONFIG);
        }
        setResendModalOpen(false);
        fetchHistory();
      } else {
        toast.error(data.message || "Failed to resend", TOAST_CONFIG);
      }
    } catch {
      toast.error("Something went wrong", TOAST_CONFIG);
    } finally {
      setIsResending(false);
    }
  };

  const handleStatusToggle = (value) => {
    setSelectedStatus((prev) => {
      const next = prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
      setSelectAllStatus(next.length === INVITE_STATUS_OPTIONS.length);
      return next;
    });
    setCurrentPage(1);
  };

  const handleSelectAllStatus = () => {
    if (selectAllStatus) {
      setSelectedStatus([]);
      setSelectAllStatus(false);
    } else {
      setSelectedStatus(INVITE_STATUS_OPTIONS.map((s) => s.value));
      setSelectAllStatus(true);
    }
    setCurrentPage(1);
  };

  const onDateChange = (range) => {
    if (!range || !range.startDate || !range.endDate) {
      setSelectedRange(null);
    } else {
      setSelectedRange([
        moment(range.startDate).startOf("day").toDate(),
        moment(range.endDate).endOf("day").toDate(),
      ]);
    }
    setShowCalendar(false);
    setCurrentPage(1);
  };

  const displayedStatus = useMemo(() => {
    if (selectedStatus.length === 0) return "None";
    if (selectedStatus.length === INVITE_STATUS_OPTIONS.length) return "All";
    return INVITE_STATUS_OPTIONS.filter((s) => selectedStatus.includes(s.value)).map((s) => s.label).join(", ");
  }, [selectedStatus]);

  const fadeIn = { hidden: { opacity: 0, scale: 0.97 }, visible: { opacity: 1, scale: 1 } };

  return (
    <>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
      <motion.div initial="hidden" animate="visible" variants={fadeIn} transition={{ duration: 0.18 }}>
        <div className="user-outer-overview invoice-outer-invoice invite-history">

          {/* Filter row — mirrors Tenant Management invoice-filter */}
          <div className="invoice-filter">
            <div className="invoice-filter-inner-section no-flter-all">

              {/* Date Range */}
              <div className="filter-inner due-date">
                <label className="calendar-icon">Date Range</label>
                <div className="date-picker-outer date-picker-outer-all" ref={calendarRef}>
                  <div className="custom-picker-icon custom-picker-icon-all">
                    <svg width="1em" height="1em" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true" focusable="false" className="rs-picker-toggle-caret rs-icon">
                      <path d="M1 4v8.5a.5.5 0 00.5.5H8c0-.128.049-.256.146-.354.555-.555.854-1.6.854-3.146a.5.5 0 01.621-.485l.119.03A2.623 2.623 0 0012.999 6.5V4h-12zm3-2h6V0h1v2h1.5A1.5 1.5 0 0114 3.5v3a3.623 3.623 0 01-4.015 3.603c-.064 1.245-.335 2.212-.831 2.898H12.5a.5.5 0 00.5-.5v-2a.5.5 0 011 0v2a1.5 1.5 0 01-1.5 1.5h-11a1.5 1.5 0 01-1.5-1.5v-9a1.5 1.5 0 011.5-1.5H3v-2h1v2zm5 4V5h1v1h2v1h-2v1H9V7H7v2h1v1H7v2H6v-2H4v2H3v-2H1V9h2V7H1V6h2V5h1v1h2V5h1v1h2zM6 9V7H4v2h2z" />
                    </svg>
                  </div>
                  <div className="custoom-icon-calender custoom-icon-calender-all">
                    <div className="custoom-icon-calender custoom-icon-calender-all custom-date-range">
                      <input
                        type="text"
                        value={formatDateRange(selectedRange)}
                        placeholder="All Time"
                        onClick={() => setShowCalendar((v) => !v)}
                        autoComplete="off"
                        readOnly
                      />
                    </div>
                    <div className="date-range-new left-align">
                      {showCalendar && (
                        <Calendar
                          onCancel={() => setShowCalendar(false)}
                          onChange={onDateChange}
                          initialRange={{
                            startDate: selectedRange ? new Date(selectedRange[0]) : null,
                            endDate: selectedRange ? new Date(selectedRange[1]) : null,
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Invite Status */}
              <div className="filter-inner user-category">
                <label>Invite Status</label>
                <div className="custom-dropdown1" ref={statusDropdownRef}>
                  <div className="drop-down-inner">
                    <div className="selected-options1" onClick={() => setStatusDropdownOpen((v) => !v)}>
                      {selectedStatus.length === 0 ? (
                        <div className="placeholder">Select</div>
                      ) : (
                        <div className="selected-option1">
                          {displayedStatus}
                        </div>
                      )}
                    </div>
                    {statusDropdownOpen && (
                      <div className="dropdown-list1">
                        <label className="checkbox-label1">
                          <input type="checkbox" checked={selectAllStatus} onChange={handleSelectAllStatus} />
                          All
                        </label>
                        {INVITE_STATUS_OPTIONS.map((opt) => (
                          <label className="checkbox-label1" key={opt.value}>
                            <input
                              type="checkbox"
                              checked={selectedStatus.includes(opt.value)}
                              onChange={() => handleStatusToggle(opt.value)}
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>

            {/* Search — right side, matches Tenant Management */}
            <div className="search-sec search-sec-user-overview">
              <div className="search-container">
                <input
                  type="text"
                  onChange={(e) => setSearchFilter(e.target.value)}
                  value={searchFilter}
                  className="search-input"
                  placeholder="Search"
                />
              </div>
            </div>
          </div>

          {/* Bulk delete bar */}
          {selectedInvites.size > 0 && (
            <div className="bulk-delete-bar">
              <span>{selectedInvites.size} item{selectedInvites.size > 1 ? "s" : ""} selected</span>
              <button type="button" className="bulk-delete-btn" onClick={() => setIsDeleteConfirmOpen(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
                  <path d="M1 4h12M4.333 4V2.667A1.333 1.333 0 015.667 1.333h2.666A1.333 1.333 0 019.667 2.667V4m2 0v9.333A1.333 1.333 0 0110.333 14.667H3.667A1.333 1.333 0 012.333 13.333V4h9.334z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Delete Selected
              </button>
              <button type="button" className="bulk-deselect-btn" onClick={() => setSelectedInvites(new Set())}>
                Cancel
              </button>
            </div>
          )}

          {/* Table card */}
          <div className="card-outer1">
            <div className="card-outer">
              <div className="card-outer-user">
                <h3>Invite History</h3>
                <div className="filter-inner showing-result">
                  <label>Showing</label>
                  <div ref={showingDropdownRef} className="select-modal-mainM custom-dropdown-user-new-outer">
                    <button
                      type="button"
                      className="selected-option custom-dropdown-user-new"
                      onClick={() => setShowingDropdownOpen((v) => !v)}
                      style={{ background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left" }}
                    >
                      {total < DEFAULT_ROWS_PER_PAGE ? total.toString() : rowsPerPage.toString()}
                    </button>
                    {showingDropdownOpen && (
                      <div className="custom-dropdown-new">
                        <ul className="showing-options-list">
                          {ROWS_PER_PAGE_OPTIONS.map((option) =>
                            total >= option ? (
                              <li
                                key={option}
                                className={rowsPerPage === option ? "selected-item" : ""}
                                onClick={() => { setRowsPerPage(option); setCurrentPage(1); setShowingDropdownOpen(false); }}
                              >
                                {option}
                              </li>
                            ) : null
                          )}
                          {total > DEFAULT_ROWS_PER_PAGE && (
                            <li
                              className={rowsPerPage === total ? "selected-item" : ""}
                              onClick={() => { setRowsPerPage(total); setCurrentPage(1); setShowingDropdownOpen(false); }}
                            >
                              ALL
                            </li>
                          )}
                          {total < DEFAULT_ROWS_PER_PAGE && (
                            <li
                              className={rowsPerPage === total ? "selected-item" : ""}
                              onClick={() => { setRowsPerPage(total); setCurrentPage(1); setShowingDropdownOpen(false); }}
                            >
                              {total}
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                  <label>of {total} Results</label>
                </div>
              </div>
              <div className="time-table">
                {isLoading ? (
                  <div className="outter-load-table">
                    <ThreeDots height="80" width="80" radius="9" color="#405BFF" ariaLabel="loading" visible />
                  </div>
                ) : (
                  <table style={{ minWidth: "50rem", width: "100%", textAlign: "left" }} className={isUpdating ? "loading-fade" : ""}>
                    <thead>
                      <tr>
                        <th className="invite-check-col">
                          <input
                            type="checkbox"
                            className="invite-checkbox"
                            checked={invites.length > 0 && selectedInvites.size === invites.length}
                            onChange={toggleSelectAll}
                          />
                        </th>
                        <th>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            Email
                            <button type="button" onClick={() => handleSort("invEmail")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
                              <img src={UpArrowImage} alt="sort" />
                            </button>
                          </div>
                        </th>
                        <th>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            Date Sent
                            <button type="button" onClick={() => handleSort("created_at")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
                              <img src={UpArrowImage} alt="sort" />
                            </button>
                          </div>
                        </th>
                        <th>
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            Status
                            <button type="button" onClick={() => handleSort("invite_status")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
                              <img src={UpArrowImage} alt="sort" />
                            </button>
                          </div>
                        </th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invites.length === 0 ? (
                        <tr>
                          <td colSpan={5} style={{ textAlign: "center" }}>No Data Available</td>
                        </tr>
                      ) : (
                        invites.map((row) => (
                          <tr key={row._id} className={selectedInvites.has(row._id) ? "invite-row-selected" : ""}>
                            <td className="invite-check-col">
                              <input
                                type="checkbox"
                                className="invite-checkbox"
                                checked={selectedInvites.has(row._id)}
                                onChange={() => toggleSelect(row._id)}
                              />
                            </td>
                            <td>{row.invEmail || "N/A"}</td>
                            <td>{formatDate(row.created_at)}</td>
                            <td>{renderStatus(row.invite_status)}</td>
                            <td>
                              {String(row.invite_status) !== "1" && (
                                <button
                                  type="button"
                                  className="cta-link resend-icon"
                                  onClick={() => { setResendEmail(row.invEmail); setResendModalOpen(true); }}
                                >
                                  <span className="resend-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="13" viewBox="0 0 19 13" fill="none">
                                      <path d="M1.34791 1.00037C1.18428 1.01562 1.04838 1.13217 1.01038 1.29075C0.972386 1.44898 1.04085 1.61399 1.18017 1.70005L7.23648 5.51864L5.43931 9.96576C5.38522 10.105 5.41843 10.2629 5.52454 10.3686C5.63032 10.4747 5.78916 10.5096 5.93053 10.4577L9.07576 9.24812L10.2379 11.7802C10.2961 11.9042 10.417 11.9876 10.5546 11.9987C10.6922 12.0099 10.8254 11.9466 10.9027 11.8337L17.8042 1.58756C17.8809 1.47134 17.8874 1.32327 17.8213 1.20096C17.7553 1.07864 17.6269 1.00172 17.4869 1.00071H1.38385C1.37187 1.00003 1.35989 0.999692 1.34791 1.00037ZM2.69596 1.75934H15.5398L7.74003 4.93755L2.69596 1.75934ZM16.4681 2.19813L10.6511 10.8491L9.63888 8.64942C9.63272 8.62875 9.6245 8.60909 9.61491 8.59012L9.60875 8.57826V8.57216L8.22476 5.56608L16.4681 2.19813ZM7.73387 6.32505L8.75843 8.56064L6.47003 9.43821L7.73387 6.32505Z" fill="#405BFF" stroke="#405BFF" strokeWidth="0.3" />
                                    </svg>
                                  </span>
                                  <span>Resend</span>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              {invites.length > 0 && totalPages > 1 && (
                <ul className="pagination">
                  {currentPage > 1 && (
                    <li className="back">
                      <button type="button" onClick={() => handlePageChange(currentPage - 1)}>Back</button>
                    </li>
                  )}
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || (p >= currentPage - PAGINATION_WINDOW_SIZE && p <= currentPage + PAGINATION_WINDOW_SIZE))
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p !== arr[idx - 1] + 1) acc.push("...");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === "..." ? (
                        <li key={`ellipsis-${idx}`} className="ellipsis">
                          <button type="button" disabled>...</button>
                        </li>
                      ) : (
                        <li key={item} className={currentPage === item ? "active" : ""}>
                          <button type="button" onClick={() => handlePageChange(item)}>{item}</button>
                        </li>
                      )
                    )}
                  {currentPage < totalPages && invites.length >= rowsPerPage && (
                    <li className="next">
                      <button type="button" onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>

          {/* Resend Modal */}
          <Modal
            isOpen={resendModalOpen}
            onRequestClose={!isResending ? () => setResendModalOpen(false) : undefined}
            contentLabel="Resend Tenant Invitation"
            className="user-invite-modal-main-outer resend-modal-outer"
            style={{ content: { height: "30%", width: "30%", position: "fixed", top: "50%", left: "50%" } }}
          >
            <div className="modal resend-modal">
              <div className="modal-top-section-user-invite">
                <div className="modal-invite-head">
                  <h2>Resend Invitation</h2>
                </div>
                <div className="modal-close-invite outter-modal-main outter-modal-main-user">
                  <button
                    type="button"
                    onClick={() => setResendModalOpen(false)}
                    disabled={isResending}
                    style={{ pointerEvents: isResending ? "none" : "auto", opacity: isResending ? 0.6 : 1, background: "none", border: "none", cursor: "pointer" }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M1 13L13 1" stroke="black" />
                      <path d="M1 0.999999L13 13" stroke="black" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="the-modal-inuser">
                <div className="user-add-outer-innner">
                  <div className="invite-user">
                    <label>Email Address</label>
                    <input type="email" value={resendEmail} disabled />
                  </div>
                </div>
              </div>
              <div className="invite-mail-popup-btn">
                <button type="button" onClick={handleResend} disabled={isResending}>
                  {isResending ? "Sending..." : "Resend Invite"}
                </button>
              </div>
            </div>
          </Modal>

          {/* Delete Confirm Modal */}
          <Modal
            className="delete-modal"
            isOpen={isDeleteConfirmOpen}
            onRequestClose={() => !isDeleting && setIsDeleteConfirmOpen(false)}
            contentLabel="Confirm Delete"
            style={{ overlay: { zIndex: 10000, backgroundColor: "rgba(0,0,0,0.45)" } }}
          >
            <div className="delete-modal-content">
              <h2>Delete Invite{selectedInvites.size > 1 ? "s" : ""}?</h2>
              <p>This will permanently delete {selectedInvites.size} invite{selectedInvites.size > 1 ? "s" : ""}. This action cannot be undone.</p>
              <div className="delete-modal-buttons">
                <button type="button" className="buttons-rejected" onClick={handleBulkDelete} disabled={isDeleting}>
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
                <button type="button" onClick={() => setIsDeleteConfirmOpen(false)} disabled={isDeleting}>
                  Cancel
                </button>
              </div>
            </div>
          </Modal>

        </div>
      </motion.div>
    </>
  );
};

export default TenantInviteHistory;
